import * as openai from "openai";
import * as Core from "openai/core";
import { Beta } from "openai/resources/beta/beta";

import { OpenPipeMeta } from "./shared";
import {
  ChatCompletionParseParams,
  Completions,
  ParsedChatCompletion,
} from "openai/resources/beta/chat/completions";
import {
  ExtractParsedContentFromParams,
  parseChatCompletion,
  validateInputTools,
} from "openai/lib/parser";
import { Chat } from "openai/resources/beta/chat/chat";
import { ContentFilterFinishReasonError, LengthFinishReasonError } from "openai/error";

export class WrappedBeta extends Beta {
  constructor(client: openai.OpenAI) {
    super(client);
    this.chat = new WrappedBetaChat(client);
  }

  chat: WrappedBetaChat;
}

export class WrappedBetaChat extends Chat {
  constructor(client: openai.OpenAI) {
    super(client);
    this.completions = new WrappedBetaCompletions(client);
  }

  completions: WrappedBetaCompletions;
}

export class WrappedBetaCompletions extends Completions {
  constructor(client: openai.OpenAI) {
    super(client);
  }
  parse<Params extends ChatCompletionParseParams, ParsedT = ExtractParsedContentFromParams<Params>>(
    body: Params,
    options?: Core.RequestOptions,
  ): Core.APIPromise<ParsedChatCompletion<ParsedT> & { openpipe?: OpenPipeMeta }> {
    validateInputTools(body.tools);
    const errorMessage = `OpenPipe cannot guarantee json schema for ${body.model}. Use the "chat.completions.create()" API instead.`;

    return this._client.chat.completions
      .create(body, {
        ...options,
        headers: {
          ...options?.headers,
          "X-Stainless-Helper-Method": "beta.chat.completions.parse",
        },
      })
      .then((response) => {
        // We can only guarantee json schema for OpenAI models. But we do not know if we are calling an OpenAI ft model, and any other model.
        // Therefore, we either disable this feature for all openpipe models, including OpenAI models, or we throw errors during the runtime.
        if (body.model.startsWith("anthropic:") || body.model.startsWith("gemini:")) {
          throw new Error(errorMessage);
        }
        for (const choice of response.choices) {
          // If the model stops generating tokens due to length or content filter, we throw the same errors as parseChatCompletion
          if (choice.finish_reason === "length") {
            throw new LengthFinishReasonError();
          }
          if (choice.finish_reason === "content_filter") {
            throw new ContentFilterFinishReasonError();
          }

          if (choice.message.content) {
            try {
              JSON.parse(choice.message.content);
            } catch (e) {
              throw new Error(errorMessage);
            }
          }
        }
        try {
          return parseChatCompletion(response, body);
        } catch (e) {
          throw new Error(errorMessage);
        }
      }) as Core.APIPromise<ParsedChatCompletion<ParsedT> & { openpipe?: OpenPipeMeta }>;
  }
}
